import React, { FC, useEffect, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArchivalCollection } from 'types';
import AidItem from './AidItem';
import { useEadTreeContext } from './EadTreeCtx';
import { Theme, createStyles, makeStyles } from '@material-ui/core';
import { useArchivalAidsListByCollection } from './ctx-utils';
import { trimLabel } from '../upper-panel/grid-upper-panel';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

type Props = {
  collection: ArchivalCollection;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    active: {
      backgroundColor: theme.palette.primary.dark,
      color: 'white!important',
      //padding: '0 4px',
    },
    link: {
      color: 'white',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    listItem: {
      fontSize: 13,
      lineHeight: '2em',
      fontWeight: 500,
      color: theme.palette.grey[300],
    },
    listItemActive: {
      fontSize: 13,
      lineHeight: '2em',
      fontWeight: 500,
      color: theme.palette.primary.light,
    },
    itemWrapper: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  })
);

const CollectionItem: FC<Props> = ({ collection }) => {
  const { id } = useParams<{ id: string }>();
  const { currentArchivalAid, crumbs, setLoading } = useEadTreeContext();
  const classes = useStyles();
  const isActive = useMemo(
    () =>
      id === collection.id ||
      crumbs?.some((c) => c.id === collection.id) ||
      currentArchivalAid?.archivalCollection?.id === collection.id,
    [id, collection, currentArchivalAid, crumbs]
  );

  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isActive) {
      setTimeout(() => {
        const el = document.getElementById('G_LEFT_PANEL');
        const top = (ref.current?.offsetTop ?? 0) - 100;
        el?.scrollTo({
          top,
          behavior: 'smooth',
        });
      }, 10);
    }
  }, [ref, isActive]);

  const { loading: aidsListLoading, aids } = useArchivalAidsListByCollection(
    collection.id,
    isActive
  );
  useEffect(() => {
    setLoading?.(aidsListLoading);
  }, [aidsListLoading, setLoading]);

  const listItemClass = isActive ? classes.listItemActive : classes.listItem;

  return (
    <div ref={ref}>
      <Link
        className={`${classes.link} ${listItemClass}`}
        to={`/fondy/0/${collection.id}/`}
      >
        <div className={classes.itemWrapper}>
          {trimLabel(collection.title ?? collection.name)}

          {collection.hasChildren ? (
            isActive ? (
              <ExpandLessIcon
                classes={{
                  root: listItemClass,
                }}
              />
            ) : (
              <ExpandMoreIcon
                classes={{
                  root: listItemClass,
                }}
              />
            )
          ) : null}
        </div>
      </Link>
      {isActive && (
        <>
          {(aids ?? []).map((aid, index) => (
            <AidItem key={`${aid.id}-${index}`} aid={aid} level={1} />
          ))}
        </>
      )}
    </div>
  );
};

export default CollectionItem;
